/**
 * API SERVICE - the single place the website talks to the backend
 * ---------------------------------------------------------------
 * Every page imports functions from here rather than calling the server
 * directly. That way, if an address ever changes, it changes once.
 *
 * Two helpful things happen automatically:
 *   1. Your login token is attached to every request.
 *   2. Errors are turned into a plain, readable message.
 */

import axios from 'axios';
import { currentLanguage } from '../hooks/useI18n';

/**
 * WHERE THE BACKEND IS.
 *
 * In development the site runs on :5173 and the API on :3000, so it needs the
 * full address. In production they are THE SAME SERVER - backend/server.js
 * serves the built React app when SERVE_FRONTEND=true - so a relative path is
 * not only enough, it is the only correct answer.
 *
 * This used to fall back to localhost:3000 in both cases, and that shipped.
 * The address is baked into the bundle at BUILD time, so every visitor's
 * browser was told to call port 3000 on THEIR OWN machine. It worked
 * perfectly for the one person who happened to have the backend running
 * locally, and the live site showed "Could not reach the server" to everybody
 * else - including on the marketplace, which is the first page anybody sees.
 *
 * Keyed off import.meta.env.PROD rather than a build argument on purpose: an
 * argument has to be remembered in the Dockerfile, in CI, and by whoever
 * deploys next. This is right by default and cannot be forgotten.
 */
const BASE_URL =
  import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:3000/api');
const TOKEN_KEY = 'choreconnect_token';

// ---------------------------------------------------------------------------
// Token storage (kept in the browser so you stay logged in after a refresh)
// ---------------------------------------------------------------------------
export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

/**
 * THE NAME OF THE "YOUR SESSION IS OVER" ANNOUNCEMENT.
 *
 * When the server tells us a login is finished - expired, signed out
 * everywhere, or SUSPENDED - this file throws the token away. That is not
 * enough on its own: React keeps its own copy of "who is logged in", and
 * emptying localStorage does not touch it. Until this existed, a banned
 * person's screen carried on showing their name, their dashboard link and
 * their bell, because nothing had told the app anything had happened. They
 * only found out when they tried to do something and got "You need to be
 * logged in to do that" - which reads as the site being broken rather than
 * as having been banned.
 *
 * So we announce it to the whole page, and useAuth.jsx listens.
 *
 * It is a browser event rather than a direct call ON PURPOSE. useAuth.jsx
 * already imports this file, so this file cannot import useAuth.jsx back -
 * the two would depend on each other and neither would load. An event is the
 * one-way door between them: this side shouts, that side listens, and neither
 * has to know the other exists.
 */
export const SESSION_ENDED = 'choreconnect:session-ended';

/**
 * "SOMETHING I JUST DID SHOULD CHANGE A BADGE IN THE NAVBAR."
 *
 * The bell and the speech bubble are counted by the navbar on a timer, and
 * a timer is right for news arriving from somebody else. It is wrong for
 * your own actions: reading a notification and watching the number sit
 * there for another twenty seconds reads as the click not having worked,
 * which is exactly what it was reported as.
 *
 * So anything that knowingly changes a count says so, and the navbar
 * re-counts at once. A browser event for the same reason as SESSION_ENDED:
 * the navbar and these pages have no other way to reach each other, and
 * neither should have to import the other.
 */
export const BADGES_CHANGED = 'choreconnect:badges-changed';

/** Ask the navbar to re-count now. Safe to call from anywhere. */
export function badgesChanged() {
  window.dispatchEvent(new CustomEvent(BADGES_CHANGED));
}

const client = axios.create({ baseURL: BASE_URL });

// Attach the token to every outgoing request.
client.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Turn every failure into a tidy Error with a readable .message.
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      // An expired, revoked or closed-account token: drop it so the app falls
      // back to the logged-out site instead of retrying forever.
      //
      // A SUSPENDED account (403) is cleared too, but the message travels on
      // in the error so the page can explain why rather than just booting
      // them to a blank login form.
      const finished = status === 401 || data?.code === 'ACCOUNT_SUSPENDED';
      if (finished && tokenStore.get()) {
        tokenStore.clear();
        // Say so out loud, so the screen stops looking logged in. The ban
        // details travel with it - the reason and how long is left - because
        // the login screen draws a countdown from them and somebody who has
        // just been shut out is owed the reason, not a blank form.
        //
        // Note this fires from whichever request happened to be in flight,
        // and the bell's half-minute check is usually the one that finds it.
        // That is deliberate: it means a ban lands on somebody sitting doing
        // nothing, not only on somebody who clicks something.
        window.dispatchEvent(
          new CustomEvent(SESSION_ENDED, {
            detail: { code: data?.code, message: data?.message, ban: data?.ban },
          })
        );
      }

      const friendly = new Error(data?.message || 'Something went wrong. Please try again.');
      friendly.status = status;
      friendly.fieldErrors = data?.errors || [];
      friendly.code = data?.code;
      // A ban carries its pieces - reason, end date, whether it is permanent -
      // rather than one ready-made sentence, because the login screen draws a
      // countdown from it. See LoginPage.jsx.
      friendly.ban = data?.ban;
      return Promise.reject(friendly);
    }

    const offline = new Error(
      'Could not reach the server. Is the backend running on port 3000?'
    );
    offline.status = 0;
    return Promise.reject(offline);
  }
);

/** Turns { a: 1, b: '' } into "?a=1", dropping anything empty. */
function toQuery(params = {}) {
  const clean = Object.entries(params).filter(
    ([, value]) => value !== '' && value !== null && value !== undefined
  );
  if (!clean.length) return '';
  return `?${new URLSearchParams(clean).toString()}`;
}

// ---------------------------------------------------------------------------
// AUTH
// ---------------------------------------------------------------------------
export const authApi = {
  // The language the site was being read in goes with the signup. It is
  // what decides which language this person's EMAIL arrives in - the
  // server writes those hours later with no browser to ask. Attached here
  // rather than in the two signup forms so neither can forget it.
  registerUser: (data) => client.post('/auth/register/user',
    { ...data, language: currentLanguage() }).then((r) => r.data),
  registerStudent: (data) => client.post('/auth/register/student',
    { ...data, language: currentLanguage() }).then((r) => r.data),
  // For a moderator this comes back WITHOUT a token: it returns
  // { twoFactorRequired: true, challenge, sentTo } and the login finishes at
  // loginTwoFactor below. See backend/routes/auth.js.
  // The language goes with it, the same as at signup: the account's own
  // setting is what decides the language of every email, including the one
  // asking a student's parent to agree. See the note in routes/auth.js.
  login: (data) => client.post('/auth/login',
    { ...data, language: currentLanguage() }).then((r) => r.data),
  me: () => client.get('/auth/me').then((r) => r.data),

  // ---- Two-step verification (moderators) ----
  // `code` is either the six emailed digits or one of the printed recovery
  // codes; the server tells them apart, so there is only one box to fill in.
  loginTwoFactor: (challenge, code) =>
    client.post('/auth/login/2fa', { challenge, code }).then((r) => r.data),
  resendTwoFactor: (challenge) =>
    client.post('/auth/login/2fa/resend', { challenge }).then((r) => r.data),
  twoFactorStatus: () => client.get('/auth/me/two-factor').then((r) => r.data),
  newRecoveryCodes: (password) =>
    client.post('/auth/me/two-factor/recovery-codes', { password }).then((r) => r.data),
  schools: () => client.get('/auth/schools').then((r) => r.data),
  // The list of towns, for the suggestions box. It can also take coordinates
  // and name the nearest town, but nothing sends them any more - a position
  // in a web address ends up in the server log. locationApi does that job.
  towns: (params) => client.get(`/auth/towns${toQuery(params || {})}`).then((r) => r.data),

  // ---- Password reset ----
  forgotPassword: (email) => client.post('/auth/forgot-password', { email }).then((r) => r.data),
  checkResetToken: (token) => client.get(`/auth/reset-password/${token}`).then((r) => r.data),
  resetPassword: (token, password) =>
    client.post(`/auth/reset-password/${token}`, { password }).then((r) => r.data),

  // Confirming an email address. The first takes the token straight out of
  // the link and needs no login - somebody opening it on their phone may well
  // not be signed in there.
  confirmEmail: (token) => client.get(`/auth/confirm-email/${token}`).then((r) => r.data),
  resendConfirmation: () => client.post('/auth/confirm-email/resend').then((r) => r.data),

  updateProfile: (data) => client.put('/auth/me', data).then((r) => r.data),
  changePassword: (data) => client.put('/auth/me/password', data).then((r) => r.data),
  deleteAccount: (password) =>
    client.delete('/auth/me', { data: { password } }).then((r) => r.data),
  removePhoto: () => client.delete('/auth/me/photo').then((r) => r.data),
  uploadPhoto: (file) => {
    const form = new FormData();
    form.append('photo', file);
    return client
      .post('/auth/me/photo', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
};

// ---------------------------------------------------------------------------
// STREET ADDRESSES (the "Use my location" button)
// ---------------------------------------------------------------------------
// Both are POSTs so a position never appears in a web address. The lookup
// happens on our own server - see backend/routes/location.js.
export const locationApi = {
  // { lat, lng } -> { address, street, number, town }
  addressAt: (position) => client.post('/location/address-at', position).then((r) => r.data),
  // { address, town } -> { lat, lng, street, number, address, exact, town }
  find: (data) => client.post('/location/find', data).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// PARENTAL CONSENT (students under 18)
// ---------------------------------------------------------------------------
export const consentApi = {
  // Used by the PARENT, from their emailed link - no login involved.
  read: (token) => client.get(`/consent/${token}`).then((r) => r.data),
  confirm: (token) => client.post(`/consent/${token}`, {}).then((r) => r.data),
  decline: (token) => client.post(`/consent/${token}`, { decline: true }).then((r) => r.data),

  // Changing their mind later. A separate, non-expiring link that lives in
  // the confirmation email - see the note by withdrawTokenHash in
  // models/Student.js for why it is not the same token as above.
  readWithdraw: (token) => client.get(`/consent/withdraw/${token}`).then((r) => r.data),
  withdraw: (token) => client.post(`/consent/withdraw/${token}`, {}).then((r) => r.data),

  // Used by the STUDENT, from their own dashboard.
  status: () => client.get('/consent/status/me').then((r) => r.data),
  resend: (guardianEmail, guardianName) =>
    client.post('/consent/resend', { guardianEmail, guardianName }).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// SAFETY - reporting, blocking, and moderation
// ---------------------------------------------------------------------------
export const safetyApi = {
  report: (data) => client.post('/safety/reports', data).then((r) => r.data),
  myReports: () => client.get('/safety/reports/mine').then((r) => r.data),

  block: (userId) => client.post('/safety/blocks', { userId }).then((r) => r.data),
  unblock: (userId) => client.delete(`/safety/blocks/${userId}`).then((r) => r.data),
  blocks: () => client.get('/safety/blocks').then((r) => r.data),

  // Moderators only - these 404 for everybody else.
  reportQueue: (status) => client.get(`/safety/admin/reports${toQuery({ status })}`).then((r) => r.data),
  handleReport: (id, status, outcome) =>
    client.patch(`/safety/admin/reports/${id}`, { status, outcome }).then((r) => r.data),
  // `opts` is { reason, days, indefinite }. Leaving days out means permanent -
  // the server treats a missing length as indefinite rather than guessing a
  // default, because a wrong guess here is somebody banned for the wrong
  // amount of time.
  suspendUser: (id, opts) =>
    client.post(`/safety/admin/users/${id}/suspend`, opts).then((r) => r.data),

  // Taking content down. Both need a reason - the owner is shown it word for
  // word. The offer one is keyed by the STUDENT, because a student has at
  // most one offer and that is how a moderator thinks about it.
  takedownChore: (id, reason) =>
    client.post(`/safety/admin/chores/${id}/takedown`, { reason }).then((r) => r.data),
  takedownOffer: (studentId, reason) =>
    client.post(`/safety/admin/students/${studentId}/offer/takedown`, { reason }).then((r) => r.data),

  // Putting them back. The list is what makes the undo reachable at all -
  // without it the only way to a hidden chore is a report that happens to
  // point at it, and offers are not attached to reports.
  takedowns: () => client.get('/safety/admin/takedowns').then((r) => r.data),
  restoreChore: (id) => client.post(`/safety/admin/chores/${id}/restore`).then((r) => r.data),
  // BY THE OFFER'S OWN ID, not the student's - a student can have three now,
  // and the Taken down tab restores exactly the row that was clicked. The
  // takedown next door is still keyed by student and takes all of them down;
  // backend/routes/safety.js explains why the two are deliberately different.
  restoreOffer: (offerId) =>
    client.post(`/safety/admin/offers/${offerId}/restore`).then((r) => r.data),
  reinstateUser: (id) => client.post(`/safety/admin/users/${id}/reinstate`).then((r) => r.data),
  auditLog: (params) => client.get(`/safety/admin/audit${toQuery(params)}`).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// CHORES
// ---------------------------------------------------------------------------
export const choreApi = {
  /**
   * No position is sent from the page at all any more (18 September 2026).
   * It used to travel in an X-Viewer-Position header - deliberately not in
   * the web address, since those are written to the server log and this can
   * be a child's position at home. Now the server measures from the address
   * on the account and nothing else, so there is nothing to send.
   */
  browse: (filters) => client.get(`/chores${toQuery(filters)}`).then((r) => r.data),
  categories: () => client.get('/chores/meta/categories').then((r) => r.data),
  mine: (status) => client.get(`/chores/mine${toQuery({ status })}`).then((r) => r.data),
  get: (id) => client.get(`/chores/${id}`).then((r) => r.data),
  create: (data) => client.post('/chores', data).then((r) => r.data),
  update: (id, data) => client.put(`/chores/${id}`, data).then((r) => r.data),
  complete: (id) => client.patch(`/chores/${id}/complete`).then((r) => r.data),
  cancel: (id, reason) => client.patch(`/chores/${id}/cancel`, { reason }).then((r) => r.data),

  // Drop ONE day out of a multi-day booking and leave the rest standing.
  // Either side may call it; the 24-hour rule applies to the student only.
  cancelVisit: (id, visitId, reason) =>
    client.patch(`/chores/${id}/visits/${visitId}/cancel`, { reason }).then((r) => r.data),
  remove: (id) => client.delete(`/chores/${id}`).then((r) => r.data),
  apply: (id, message) => client.post(`/chores/${id}/apply`, { message }).then((r) => r.data),
  applications: (id) => client.get(`/chores/${id}/applications`).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// APPLICATIONS
// ---------------------------------------------------------------------------
export const applicationApi = {
  mine: (status) => client.get(`/applications/mine${toQuery({ status })}`).then((r) => r.data),
  accept: (id) => client.patch(`/applications/${id}/accept`).then((r) => r.data),
  makeBackup: (id) => client.patch(`/applications/${id}/backup`).then((r) => r.data),
  reject: (id) => client.patch(`/applications/${id}/reject`).then((r) => r.data),
  withdraw: (id) => client.patch(`/applications/${id}/withdraw`).then((r) => r.data),
  // `reason` is required by the server when the job starts within 24 hours,
  // and ignored otherwise - see the cancel route in routes/applications.js.
  cancelJob: (id, reason = '') =>
    client.patch(`/applications/${id}/cancel`, { reason }).then((r) => r.data),

  // Walking away because it does not feel safe. Deliberately NOT the same call
  // as cancelJob: the 24-hour rule does not apply, it does not count against
  // the student, and it alerts the safety inbox. The reason is optional -
  // somebody frightened enough to leave should not have to write an essay
  // first. See routes/applications.js.
  leaveUnsafe: (id, reason) =>
    client.patch(`/applications/${id}/leave`, { reason }).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// PEOPLE
// ---------------------------------------------------------------------------
export const studentApi = {
  browse: (filters) => client.get(`/students${toQuery(filters)}`).then((r) => r.data),
  get: (id) => client.get(`/students/${id}`).then((r) => r.data),
  dashboard: () => client.get('/students/dashboard').then((r) => r.data),
  // How many students are ready to work, and at what - numbers only, never
  // names. Posters only; see routes/students.js.
  counts: () => client.get('/students/counts').then((r) => r.data),
};

export const userApi = {
  // Moderators only - the server refuses it to anybody else.
  browse: (filters) => client.get(`/users${toQuery(filters)}`).then((r) => r.data),
  get: (id) => client.get(`/users/${id}`).then((r) => r.data),
  dashboard: () => client.get('/users/dashboard').then((r) => r.data),
};

// ---------------------------------------------------------------------------
// REVIEWS
// ---------------------------------------------------------------------------
export const reviewApi = {
  create: (data) => client.post('/reviews', data).then((r) => r.data),
  pending: () => client.get('/reviews/pending').then((r) => r.data),
  forUser: (userId, page) => client.get(`/reviews/user/${userId}${toQuery({ page })}`).then((r) => r.data),
  forChore: (choreId) => client.get(`/reviews/chore/${choreId}`).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// NOTIFICATIONS
// ---------------------------------------------------------------------------
export const notificationApi = {
  list: (limit) => client.get(`/notifications${toQuery({ limit })}`).then((r) => r.data),
  unreadCount: () => client.get('/notifications/unread').then((r) => r.data),
  markRead: (id) => client.patch(`/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () => client.patch('/notifications/read-all').then((r) => r.data),
  remove: (id) => client.delete(`/notifications/${id}`).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// PAYMENTS
// ---------------------------------------------------------------------------
export const paymentApi = {
  config: () => client.get('/payments/config').then((r) => r.data),
  status: () => client.get('/payments/status').then((r) => r.data),
  checkout: () => client.post('/payments/checkout').then((r) => r.data),
  verify: (sessionId) => client.get(`/payments/verify${toQuery({ session_id: sessionId })}`).then((r) => r.data),
  portal: () => client.post('/payments/portal').then((r) => r.data),
  devActivate: () => client.post('/payments/dev-activate').then((r) => r.data),
};

/**
 * Offers - a student saying what they can do and when they are free.
 * Browsing is posters only; publishing is students only. The server enforces
 * both, so these calls simply fail for the wrong role rather than being hidden.
 */
export const offerApi = {
  list: (params) => client.get(`/offers${toQuery(params)}`).then((r) => r.data),
  one: (id) => client.get(`/offers/${id}`).then((r) => r.data),

  /**
   * A student's own side. `mine` returns ALL their offers - up to three since
   * 25 August 2026 - plus the single timetable they share.
   *
   * The timetable has its own call because it belongs to the person rather
   * than to any one offer. Saving an offer must not quietly rewrite their
   * week, and changing their week must not require opening an offer.
   */
  mine: () => client.get('/offers/mine').then((r) => r.data),
  saveAvailability: (weekly) => client.put('/offers/availability', { weekly }).then((r) => r.data),

  publish: (payload) => client.post('/offers', payload).then((r) => r.data),
  update: (id, payload) => client.put(`/offers/mine/${id}`, payload).then((r) => r.data),
  setStatus: (id, status) =>
    client.patch(`/offers/mine/${id}/status`, { status }).then((r) => r.data),
  remove: (id) => client.delete(`/offers/mine/${id}`).then((r) => r.data),

  requests: () => client.get('/offers/requests').then((r) => r.data),
  request: (id, payload) => client.post(`/offers/${id}/request`, payload).then((r) => r.data),
  accept: (id) => client.patch(`/offers/requests/${id}/accept`).then((r) => r.data),
  decline: (id) => client.patch(`/offers/requests/${id}/decline`).then((r) => r.data),
  withdraw: (id) => client.patch(`/offers/requests/${id}/withdraw`).then((r) => r.data),
};

/**
 * Lock-screen notifications. See services/push.js for the browser side - this
 * is only the four calls to our own server.
 */
/**
 * Messages about one agreed job. Only ever between the poster and the student
 * who accepted it - the server enforces that, and answers 404 to anybody else
 * so that trying ids tells you nothing.
 */
export const messageApi = {
  /**
   * `withStudent` says WHICH conversation about a chore, because a poster can
   * have one with each student who asked about the same job. A student never
   * passes it: there is only ever one of theirs. See backend/routes/messages.js.
   */
  threads: (archived = false) => client
    .get(`/messages${archived ? '?archived=1' : ''}`).then((r) => r.data),
  unread: () => client.get('/messages/unread').then((r) => r.data),
  thread: (choreId, withStudent) => client
    .get(`/messages/${choreId}${toQuery({ with: withStudent })}`).then((r) => r.data),
  send: (choreId, body, withStudent) => client
    .post(`/messages/${choreId}${toQuery({ with: withStudent })}`, { body }).then((r) => r.data),
  markRead: (choreId, withStudent) => client
    .patch(`/messages/${choreId}/read${toQuery({ with: withStudent })}`).then((r) => r.data),
  archive: (choreId, withStudent) => client
    .patch(`/messages/${choreId}/archive${toQuery({ with: withStudent })}`).then((r) => r.data),
  unarchive: (choreId, withStudent) => client
    .patch(`/messages/${choreId}/unarchive${toQuery({ with: withStudent })}`).then((r) => r.data),

  /**
   * THE SAME FIVE, FOR A QUESTION ABOUT A STUDENT'S OFFER.
   *
   * `withPoster` rather than `withStudent`, and that is the whole difference:
   * a job has one poster and many students asking, an offer has one student
   * and many adults asking. So one is named by the student and the other by
   * the adult. A student reading theirs passes it; the adult never does,
   * because there is only ever one of theirs.
   */
  offerThread: (offerId, withPoster) => client
    .get(`/messages/offer/${offerId}${toQuery({ with: withPoster })}`).then((r) => r.data),
  offerSend: (offerId, body, withPoster) => client
    .post(`/messages/offer/${offerId}${toQuery({ with: withPoster })}`, { body }).then((r) => r.data),
  offerMarkRead: (offerId, withPoster) => client
    .patch(`/messages/offer/${offerId}/read${toQuery({ with: withPoster })}`).then((r) => r.data),
  offerArchive: (offerId, withPoster) => client
    .patch(`/messages/offer/${offerId}/archive${toQuery({ with: withPoster })}`).then((r) => r.data),
  offerUnarchive: (offerId, withPoster) => client
    .patch(`/messages/offer/${offerId}/unarchive${toQuery({ with: withPoster })}`).then((r) => r.data),

  // Moderators only, and only for conversations that were reported or where
  // the contact-details detector fired. Everything else answers 404 - the
  // server decides that, not the screen. See the note above the moderation
  // block in backend/routes/messages.js.
  flaggedThreads: () => client.get('/messages/admin/threads').then((r) => r.data),
  // `withStudent` names WHICH conversation about the job - a job can have the
  // one with the student doing it and any number of questions. Left out, it
  // is the student doing it.
  readThread: (choreId, withStudent) => client
    .get(`/messages/admin/thread/${choreId}${toQuery({ with: withStudent })}`).then((r) => r.data),
  readOfferThread: (offerId, withPoster) => client
    .get(`/messages/admin/thread/offer/${offerId}${toQuery({ with: withPoster })}`).then((r) => r.data),
  hideMessage: (messageId, hidden) =>
    client.patch(`/messages/admin/${messageId}/hide`, { hidden }).then((r) => r.data),
};

export const pushApi = {
  key: () => client.get('/push/key').then((r) => r.data),
  subscribe: (subscription) => client.post('/push/subscribe', subscription).then((r) => r.data),
  unsubscribe: (endpoint) =>
    client.delete('/push/subscribe', { data: endpoint ? { endpoint } : {} }).then((r) => r.data),
  test: () => client.post('/push/test').then((r) => r.data),
};

/**
 * SETTINGS - the switches a moderator can flip without a deploy.
 *
 * `read` is public on purpose: the people who most need to know that email is
 * off are signed out, because they are the ones about to ask for a password
 * reset. See backend/routes/settings.js.
 */
export const settingsApi = {
  read: () => client.get('/settings').then((r) => r.data),
  setEmailsOffline: (offline) =>
    client.patch('/settings/emails-offline', { offline }).then((r) => r.data),
};

/**
 * REPORT AN ISSUE - one conversation per person, with whoever is moderating.
 *
 * The person never learns which moderator answered; the server strips it, so
 * there is nothing here that could leak it. See backend/models/SupportMessage.js.
 */
export const supportApi = {
  thread: () => client.get('/support').then((r) => r.data),
  send: (body) => client.post('/support', { body }).then((r) => r.data),
  markRead: () => client.patch('/support/read').then((r) => r.data),
  unread: () => client.get('/support/unread').then((r) => r.data),

  // Moderators only - these 404 for everybody else.
  adminThreads: () => client.get('/support/admin/threads').then((r) => r.data),
  adminThread: (userId) => client.get(`/support/admin/${userId}`).then((r) => r.data),
  adminReply: (userId, body) => client.post(`/support/admin/${userId}`, { body }).then((r) => r.data),
  adminMarkRead: (userId) => client.patch(`/support/admin/${userId}/read`).then((r) => r.data),
};

export const donationApi = {
  config: () => client.get('/donations/config').then((r) => r.data),
  checkout: (amountCents) => client.post('/donations/checkout', { amountCents }).then((r) => r.data),
  verify: (sessionId) => client.get(`/donations/verify${toQuery({ session_id: sessionId })}`).then((r) => r.data),
};

export default client;
